import sanitizeHtml from "sanitize-html";

// Define blacklisted tags and attributes as constants with camelCase
//IMPORTANT To be align with TrustCenter Visitor and Chrome extension
const blacklistedTags = [
  "script",
  "iframe",
  "object",
  "embed",
  "meta",
  "base",
  "applet",
  "frame",
  "frameset",
  "audio",
  "video",
  "canvas",
];

//IMPORTANT To be align with TrustCenter Visitor and Chrome extension
const blacklistedAttributes = [
  "onerror",
  "onload",
  "onclick",
  "onmouseover",
  "onmouseout",
  "onmousedown",
  "onmouseup",
  "onkeydown",
  "onkeypress",
  "onkeyup",
  "onchange",
  "onfocus",
  "onblur",
  "onsubmit",
  "onreset",
  "formaction",
  "javascript:",
  "eval",
  "expression",
  "vbscript",
  "data-custom-unsafe",
];

const allowedSchemes = ["http", "https", "ftp", "mailto", "tel", "data"];

// Check if a style value is potentially dangerous
//IMPORTANT To be align with TrustCenter Visitor and Chrome extension

function isUnsafeStyle(styleValue) {
  const unsafePatterns = [
    "expression",
    "javascript:",
    "vbscript:",
    "data:",
    "behavior:",
    "-moz-binding",
    "url(",
  ];
  return unsafePatterns.some((pattern) =>
    styleValue.toLowerCase().includes(pattern)
  );
}

export function cleanReferences(text) {
  //Removes all occurrences of references [1], [2] .... of a given string
  // console.log("Inside cleanReferences - text", text);
  if (_.isEmpty(text)) return "";
  let modifiedText = text.replace(/\s?\[[0-9]+\]/g, "");
  //ex : "This is a text [1] and [2]" => "This is a text and"
  // ex : "This is [1][2][3] a text" => "This is a text"
  // console.log("Inside cleanReferences - text modifiedText", text, modifiedText);
  return modifiedText;
}

export function clearMarks(text) {
  const pattern = /[⋢⋣]/g;
  return text.replace(pattern, "");
}

export function stripHtmlTags(text) {
  if (_.isEmpty(text)) return "";
  return text.replace(/<[^>]*>?/gm, "");
}

// Create sanitization configuration
//IMPORTANT To be align with TrustCenter Visitor and Chrome extension
export function createSanitizationConfig() {
  return {
    exclusiveFilter: createTagFilter(),
    disallowedTags: blacklistedTags,
    allowedAttributes: {
      "*": ["style", "class", "qa_id"],
      div: ["data-v-*"],
      img: [
        "src",
        "alt",
        "width",
        "height",
        "style",
        "data-expired",
        "data-vendor-doc-id",
        "data-width",
        "qa_id",
      ],
      table: [
        "border",
        "cellpadding",
        "cellspacing",
        "style",
        "class",
        "qa_id",
      ],
      td: ["colspan", "rowspan", "style", "class", "qa_id"],
      th: ["colspan", "rowspan", "style", "class", "qa_id"],
      a: [
        "href",
        "target",
        "rel",
        "data-refindex",
        "data-entryid",
        "data-highlight",
        "style",
        "class",
        "qa_id",
      ],
      ol: ["style", "type", "start", "class", "qa_id"],
      ul: ["style", "type", "class", "qa_id"],
      li: ["style", "value", "class", "qa_id"],
      font: ["color", "style", "class", "qa_id"],
      b: ["style", "class", "qa_id"],
      i: ["style", "class", "qa_id"],
      u: ["style", "class", "qa_id"],
      strike: ["style", "class", "qa_id"],
    },
    allowedTags: [
      "div",
      "p",
      "br",
      "b",
      "i",
      "em",
      "strong",
      "a",
      "ul",
      "ol",
      "li",
      "blockquote",
      "code",
      "pre",
      "span",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "img",
      "font",
      "u",
      "strike",
    ],
    transformTags: createTagTransformers(),
    allowedSchemes: allowedSchemes,
    allowComments: false,
  };
}

export function cleanHtml(html) {
  if (!html) return html;
  return sanitizeHtml(html, createSanitizationConfig());
}

export function stripInlineFontFamily(html) {
  if (!html || !html.includes("font-family")) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("[style]").forEach((el) => {
    el.style.removeProperty("font-family");
  });
  return doc.body.innerHTML;
}

// Filter function for blacklisted tags
//IMPORTANT To be align with TrustCenter Visitor and Chrome extension
export function createTagFilter() {
  return function (frame) {
    return blacklistedTags.includes(frame.tag);
  };
}

// Create tag transformer functions for different elements
//IMPORTANT To be align with TrustCenter Visitor and Chrome extension
export function createTagTransformers() {
  return {
    "*": function (tagName, attribs) {
      const cleanAttribs = filterAttributes(attribs);

      // Preserve strikethrough in style attributes
      if (cleanAttribs.style && cleanAttribs.style.includes("line-through")) {
        cleanAttribs.style = cleanAttribs.style;
      }

      return { tagName, attribs: cleanAttribs };
    },

    font: function (tagName, attribs) {
      const cleanAttribs = filterAttributes(attribs);
      // Ensure color attribute is preserved
      if (attribs.color) {
        cleanAttribs.color = attribs.color;
      }
      return { tagName, attribs: cleanAttribs };
    },

    table: function (tagName, attribs) {
      const cleanAttribs = filterAttributes(attribs);

      // Add default table styling
      cleanAttribs.style =
        (cleanAttribs.style || "") +
        "border-collapse: collapse; width: 100%; margin-bottom: 1rem;";

      return { tagName, attribs: cleanAttribs };
    },

    th: function (tagName, attribs) {
      const cleanAttribs = filterAttributes(attribs);

      // Add default th styling
      cleanAttribs.style =
        (cleanAttribs.style || "") +
        "border: 1px solid #ddd; padding: 8px; text-align: left; background-color: #f2f2f2;";

      return { tagName, attribs: cleanAttribs };
    },

    td: function (tagName, attribs) {
      const cleanAttribs = filterAttributes(attribs);

      // Add default td styling
      cleanAttribs.style =
        (cleanAttribs.style || "") +
        "border: 1px solid #ddd; padding: 8px; text-align: left;";

      return { tagName, attribs: cleanAttribs };
    },

    a: function (tagName, attribs) {
      const cleanAttribs = filterAttributes(attribs);
      if (attribs?.href !== "#") {
        // Avoid changing AI generated answers
        // Add target="_blank" for links
        cleanAttribs.target = "_blank";
        // Add rel="noopener noreferrer" for security
        cleanAttribs.rel = "noopener noreferrer";
        // Only add default styles if no style exists
        if (!cleanAttribs.style) {
          cleanAttribs.style = "color: blue; text-decoration: underline;";
        }
      }
      return { tagName, attribs: cleanAttribs };
    },

    ol: function (tagName, attribs) {
      const cleanAttribs = filterAttributes(attribs);
      // Ensure ordered lists show numbers
      cleanAttribs.style =
        (cleanAttribs.style || "") +
        "list-style-position: inside; list-style-type: decimal; padding-left: 2.5em;";
      cleanAttribs.class = (cleanAttribs.class || "") + " vendict-rte-list";
      return { tagName, attribs: cleanAttribs };
    },
    ul: function (tagName, attribs) {
      const cleanAttribs = filterAttributes(attribs);
      // Ensure unordered lists show discs
      cleanAttribs.style =
        (cleanAttribs.style || "") +
        "list-style-position: inside; list-style-type: disc; padding-left: 2.5em;";
      cleanAttribs.class = (cleanAttribs.class || "") + " vendict-rte-list";
      return { tagName, attribs: cleanAttribs };
    },
  };
}

// Filter attributes to remove blacklisted ones
//IMPORTANT To be align with TrustCenter Visitor and Chrome extension
export function filterAttributes(attribs) {
  const cleanAttribs = {};

  for (const attr in attribs) {
    const value = attribs[attr];

    if (!isBlacklistedAttribute(attr, value)) {
      // Additional check for style attributes
      if (attr === "style" && isUnsafeStyle(value)) {
        continue; // Skip unsafe styles
      }
      cleanAttribs[attr] = value;
    }
  }

  return cleanAttribs;
}

//IMPORTANT To be align with TrustCenter Visitor and Chrome extension
// Check if an attribute is blacklisted
export function isBlacklistedAttribute(attrName, attrValue) {
  const attrLower = attrName.toLowerCase();

  return blacklistedAttributes.some((blacklisted) => {
    return (
      attrLower === blacklisted.toLowerCase() ||
      (typeof attrValue === "string" &&
        attrValue.toLowerCase().includes(blacklisted.toLowerCase()))
    );
  });
}

export default {
  methods: {
    clearMarks(text) {
      return clearMarks(text);
    },
    cleanReferences(text) {
      return cleanReferences(text);
    },
    removeFirstBR(html) {
      if (html.indexOf("<br>") < 1) html = html.replace("<br>", "", 1);
      return html;
    },
    escapeHtml(input) {
      const replacements = {
        "<": "&lt;",
        ">": "&gt;",
      };
      return input.replace(/[<>]/g, (match) => replacements[match]);
    },
    //IMPORTANT To be align with TrustCenter Visitor and Chrome extension
    cleanHtml(html) {
      return cleanHtml(html);
    },
    removeFontTag(html, brandingCaptionColorKey) {
      // Always strip inline font-family so global font settings take priority
      let result = this.stripInlineFontFamily(html)
      let colorFromBrending =
        this.getBrending?.overview?.[brandingCaptionColorKey]
      if (!colorFromBrending) return result
      return result
        .replace(/<font\b[^>]*>/gi, '')
        .replace(/<\/font>/gi, '')
    },
    stripInlineFontFamily(html) {
      return stripInlineFontFamily(html);
    },
  },
};
